import React, { useCallback } from 'react';
import { Message } from 'botframework-directlinejs';
import { type ILogger } from '@bingads-webui/ads-copilot-shared-types';

import { StaticMessageType } from '../botTypes';
import { DefaultRenderActivity, RenderActivityParams, IAdsCopilotProps } from '../types';
import { isTypingMessage } from '../utils';
import { LoadingIntent } from '../LoadingIntent';
import { IStaticMessageProps, StaticMessage } from '../components/static-message';

export const errorMessageTypes: StaticMessageType[] = [
  'ResponsibleAI',
  'SignInError',
  'InternalError',
  'TurnLimitReached',
  'TokenExpired',
  'MissingArgumentsInPayload',
  'LargePayload',
  'Timeout',
  'ConversationExpired',
  'InvalidPayload',
  'SydneyResponsibleAI',
];

type UseStaticMessageParam = Pick<IAdsCopilotProps, 'renderActivity' | 'onFeedbackButtonClick' | 'staticMessageOverrides'> & Pick<IStaticMessageProps, 'onClickStartOver' | 'isWelcomeMessageEnabled' | 'isStaticPrimary'> & { logger?: ILogger };

export const useStaticMessage = ({
  isWelcomeMessageEnabled,
  isStaticPrimary,
  onClickStartOver,
  onFeedbackButtonClick,
  renderActivity,
  staticMessageOverrides,
  logger,
}: UseStaticMessageParam) => {
  const defaultRenderStaticMessageActivity = useCallback((next: DefaultRenderActivity) => (activity: Message, params: RenderActivityParams) => {
    if (typeof activity?.value === 'string' && activity?.value === 'AnimationCard') {
      return <LoadingIntent key={activity.id} activity={activity} />;
    }

    if (activity.text === 'StaticMessages') {
      if (isTypingMessage(activity)) {
        return null;
      }

      // render the static message
      return (
        <StaticMessage
          key={activity.id}
          activity={activity}
          onClickStartOver={onClickStartOver}
          onFeedbackButtonClick={onFeedbackButtonClick}
          isWelcomeMessageEnabled={isWelcomeMessageEnabled}
          isStaticPrimary={isStaticPrimary}
          logger={logger}
          messageOverrides={staticMessageOverrides}
        />
      );
    }

    // not a static message, pass the activity to the next renderer
    return next(activity, params);
  }, [onClickStartOver, onFeedbackButtonClick, isWelcomeMessageEnabled, isStaticPrimary, logger, staticMessageOverrides]);

  const finalRenderActivity = useCallback<NonNullable<UseStaticMessageParam['renderActivity']>>((activity, params, next) => {
    if (renderActivity) {
      return renderActivity(activity, params, defaultRenderStaticMessageActivity(next));
    }

    return defaultRenderStaticMessageActivity(next)(activity, params);
  }, [defaultRenderStaticMessageActivity, renderActivity]);

  return {
    renderActivity: finalRenderActivity,
  };
};
